import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';

import { ChannelIcon } from '@/components/ui/channel-icon';
import { Typography } from '@/components/ui/typography';
import { SourcePremiumTag } from '../popular-contract-cards';
import { LockedBoardExplanation } from './locked-board-explanation';

export interface ChannelOption {
  id: number | undefined;
  name: string;
  logoSquareUrl: string | null;
}

interface ChannelSearchDropdownProps {
  isDebouncing: boolean;
  isFetching: boolean;
  hasSearchQuery: boolean;
  options: ChannelOption[];
  selectedChannelId?: number;
  onSelect: (id: number | undefined, name: string) => void;
  listboxId: string;
  listboxLabel?: string;
  showPopular?: boolean;
  keyboardEnabled?: boolean;
  activeIndex?: number;
  isLocked?: (option: ChannelOption) => boolean;
  explanationIndex?: number;
  onExplanationClose?: () => void;
  focusSearch?: () => void;
}

function DropdownMessageCenter({
  children,
  announce = false,
}: {
  children: React.ReactNode;
  announce?: boolean;
}) {
  return (
    <div
      role={announce ? 'status' : undefined}
      className="flex h-8 items-center justify-center px-2"
    >
      <Typography
        variant="body1"
        className="!text-fx-neutral-600 w-full text-center leading-none"
      >
        {children}
      </Typography>
    </div>
  );
}

export function ChannelSearchDropdown({
  isDebouncing,
  isFetching,
  hasSearchQuery,
  options,
  selectedChannelId,
  onSelect,
  listboxId,
  listboxLabel,
  showPopular = false,
  keyboardEnabled = false,
  activeIndex = -1,
  isLocked,
  explanationIndex = -1,
  onExplanationClose,
  focusSearch,
}: ChannelSearchDropdownProps) {
  const message =
    !hasSearchQuery && !showPopular ? (
      <FormattedMessage
        defaultMessage="Type to get options"
        description="Add contract channel dropdown: prompt before user searches"
      />
    ) : isFetching || isDebouncing ? (
      <FormattedMessage
        defaultMessage="Loading options, please wait"
        description="Add contract channel dropdown: shown while search is debouncing or API is in flight"
      />
    ) : options.length === 0 ? (
      <FormattedMessage
        defaultMessage="No options found!"
        description="Add contract channel dropdown: API returned no matching channel"
      />
    ) : null;

  if (message && !keyboardEnabled) {
    return <DropdownMessageCenter>{message}</DropdownMessageCenter>;
  }

  return (
    <>
      {showPopular && !message && (
        <Typography
          variant="caption"
          className="!text-fx-neutral-600 px-2 py-1"
        >
          <FormattedMessage
            defaultMessage="Popular contracts"
            description="Frequently used job boards available to connect with an existing contract"
          />
        </Typography>
      )}
      <ul
        id={listboxId}
        role={keyboardEnabled ? 'listbox' : undefined}
        aria-label={listboxLabel}
        aria-busy={keyboardEnabled ? isFetching || isDebouncing : undefined}
        className="flex flex-col"
      >
        {!message &&
          options.map((option, index) => {
            const isSelected =
              selectedChannelId !== undefined &&
              selectedChannelId === option.id;
            const locked = isLocked?.(option) ?? false;
            const row = (
              <button
                id={keyboardEnabled ? `${listboxId}-${index}` : undefined}
                role={keyboardEnabled ? 'option' : undefined}
                aria-selected={keyboardEnabled ? isSelected : undefined}
                aria-disabled={locked || undefined}
                tabIndex={keyboardEnabled ? -1 : undefined}
                type="button"
                className={clsx(
                  'flex h-8 w-full items-center gap-2 rounded-sm px-2 text-left transition-colors',
                  'hover:bg-fx-neutral-100 focus-visible:bg-fx-neutral-100 focus-visible:outline-none',
                  isSelected && 'bg-fx-brand-50',
                  activeIndex === index && 'bg-fx-neutral-100',
                )}
                onMouseDown={e => e.preventDefault()}
                onClick={() => onSelect(option.id, option.name)}
              >
                <span aria-hidden={keyboardEnabled || undefined}>
                  <ChannelIcon
                    channelSlug={
                      keyboardEnabled ? option.name.toLowerCase() : undefined
                    }
                    channelName={option.name}
                    logoSquareUrl={option.logoSquareUrl}
                  />
                </span>
                <Typography
                  variant="body1"
                  className={clsx('truncate', locked && 'text-fx-neutral-400')}
                >
                  {option.name}
                </Typography>
                {locked && (
                  <span className="ml-auto">
                    <SourcePremiumTag />
                  </span>
                )}
              </button>
            );
            return (
              <li
                key={option.id ?? option.name}
                role={keyboardEnabled ? 'presentation' : undefined}
              >
                {locked ? (
                  <LockedBoardExplanation
                    open={explanationIndex === index}
                    onOpenChange={open => {
                      if (!open) onExplanationClose?.();
                    }}
                    focusSearch={() => focusSearch?.()}
                  >
                    {row}
                  </LockedBoardExplanation>
                ) : (
                  row
                )}
              </li>
            );
          })}
      </ul>
      {message && (
        <DropdownMessageCenter announce>{message}</DropdownMessageCenter>
      )}
    </>
  );
}
